import { type Message } from '@littlebird/sdk';
import { type Token, type Tokens } from 'marked';

import {
  chatScrollAnchorKey,
  readScrollAnchor,
  writeScrollAnchor,
} from '@/components/blocks/Message/MessageList/scrollAnchor';
import {
  chunkCodeLinesForHighlighting,
  isExpectedSyntaxHighlightError,
  requestCodeHighlight,
  shouldEagerHighlightCode,
  splitCodeLinesForHighlighting,
} from '@/components/ui/smoothdown/components/StaticCode/syntax-highlight.service';
import type { SyntaxHighlightTheme } from '@/components/ui/smoothdown/components/StaticCode/syntax-highlight.types';
import {
  parseMarkdownIntoBlocks,
  simpleHash,
  stripHiddenTags,
} from '@/components/ui/smoothdown/utils';
import { logError } from '@/lib/errorLogging';
import { uiStore } from '@/stores/ui.store';

import type { BundledLanguage } from 'shiki';

const PREWARM_PRIORITY = 10_000;
const MAX_PREWARM_CODE_BLOCKS = 8;
const MAX_PREWARM_HIGHLIGHT_JOBS = 24;
const MAX_PREWARM_SIGNATURES = 100;
const DEFAULT_CHAT_TAB_ID = 'default-tab';

type CodeBlock = {
  code: string;
  language: string;
};

type MessageContentEntry = {
  id: string;
  content: string;
};

interface PrewarmThreadSyntaxHighlightOptions {
  scrollRestorationId?: string;
}

const prewarmedThreadSignatures = new Map<string, string>();

const rememberPrewarmSignature = (threadId: string, signature: string) => {
  if (prewarmedThreadSignatures.get(threadId) === signature) return false;

  prewarmedThreadSignatures.delete(threadId);
  prewarmedThreadSignatures.set(threadId, signature);

  if (prewarmedThreadSignatures.size > MAX_PREWARM_SIGNATURES) {
    const oldestThreadId = prewarmedThreadSignatures.keys().next().value;
    if (oldestThreadId) prewarmedThreadSignatures.delete(oldestThreadId);
  }

  return true;
};

const getSyntaxHighlightTheme = (): SyntaxHighlightTheme =>
  uiStore.theme === 'dark' ? 'github-dark' : 'github-light';

export const getChatScrollRestorationId = (
  tabId: string | null | undefined,
  threadId: string
) => `chat-scroll-${tabId ?? DEFAULT_CHAT_TAB_ID}-${threadId}`;

export const setChatScrollRestorationAnchor = (
  scrollRestorationId: string,
  messageId: string
) => {
  if (typeof window === 'undefined') return;
  writeScrollAnchor(chatScrollAnchorKey(scrollRestorationId), {
    messageId,
    delta: 0,
  });
};

// The chat list saves its reading position as a message-id anchor; warm
// code blocks around it first so a restore renders highlighted content.
const readSavedAnchorMessageId = (scrollRestorationId: string | undefined) => {
  if (!scrollRestorationId || typeof window === 'undefined') return null;
  return (
    readScrollAnchor(chatScrollAnchorKey(scrollRestorationId))?.messageId ??
    null
  );
};

const orderMessageContentsForPrewarm = (
  messageContents: MessageContentEntry[],
  anchorMessageId: string | null
) => {
  const targetIndex = anchorMessageId
    ? messageContents.findIndex((entry) => entry.id === anchorMessageId)
    : -1;
  if (targetIndex === -1) {
    return messageContents.slice().reverse();
  }

  const ordered: MessageContentEntry[] = [];
  for (
    let distance = 0;
    ordered.length < messageContents.length;
    distance += 1
  ) {
    const downwardIndex = targetIndex + distance;
    if (downwardIndex < messageContents.length) {
      ordered.push(messageContents[downwardIndex]);
    }

    if (distance === 0) continue;

    const upwardIndex = targetIndex - distance;
    if (upwardIndex >= 0) {
      ordered.push(messageContents[upwardIndex]);
    }
  }

  return ordered;
};

const getCodeBlockLanguage = (code: Tokens.Code) =>
  code.lang?.trim().split(/\s+/)[0] || 'text';

const collectCodeBlocksFromTokens = (
  tokens: Token[],
  codeBlocks: CodeBlock[]
) => {
  for (let tokenIndex = tokens.length - 1; tokenIndex >= 0; tokenIndex -= 1) {
    if (codeBlocks.length >= MAX_PREWARM_CODE_BLOCKS) return;

    const token = tokens[tokenIndex];
    if (!token) continue;

    switch (token.type) {
      case 'code': {
        const code = token as Tokens.Code;
        if (code.text) {
          codeBlocks.push({
            code: code.text,
            language: getCodeBlockLanguage(code),
          });
        }
        break;
      }
      case 'blockquote':
        collectCodeBlocksFromTokens(
          (token as Tokens.Blockquote).tokens,
          codeBlocks
        );
        break;
      case 'list':
        for (
          let itemIndex = (token as Tokens.List).items.length - 1;
          itemIndex >= 0;
          itemIndex -= 1
        ) {
          const item = (token as Tokens.List).items[itemIndex];
          if (!item) continue;
          collectCodeBlocksFromTokens(item.tokens, codeBlocks);
        }
        break;
      default:
        break;
    }
  }
};

const collectCodeBlocksFromMessage = (content: string) => {
  const codeBlocks: CodeBlock[] = [];
  try {
    collectCodeBlocksFromTokens(parseMarkdownIntoBlocks(content), codeBlocks);
  } catch {
    return codeBlocks;
  }
  return codeBlocks;
};

const queueHighlightPrewarm = ({
  code,
  jobIndex,
  language,
  theme,
}: CodeBlock & {
  jobIndex: number;
  theme: SyntaxHighlightTheme;
}) => {
  const request = requestCodeHighlight({
    code,
    language: language as BundledLanguage,
    priority: PREWARM_PRIORITY + jobIndex,
    theme,
  });

  request.promise.catch((error) => {
    if (isExpectedSyntaxHighlightError(error)) return;
    logError(error, 'Failed to prewarm syntax highlighting');
  });
};

export const prewarmThreadSyntaxHighlights = (
  threadId: string,
  messages: Message[],
  options: PrewarmThreadSyntaxHighlightOptions = {}
) => {
  if (messages.length === 0) return;

  const messageContents = messages
    .map((message) => ({
      id: message.id,
      content: stripHiddenTags(message.content),
    }))
    .filter(({ content }) => content.trim().length > 0);

  if (messageContents.length === 0) return;

  const theme = getSyntaxHighlightTheme();
  const anchorMessageId = readSavedAnchorMessageId(options.scrollRestorationId);
  const signature = [
    theme,
    anchorMessageId ?? 'bottom',
    simpleHash(messageContents.map(({ content }) => content).join('\n\n')),
  ].join(':');
  if (!rememberPrewarmSignature(threadId, signature)) return;

  const codeBlocks = orderMessageContentsForPrewarm(
    messageContents,
    anchorMessageId
  )
    .flatMap(({ content }) => collectCodeBlocksFromMessage(content))
    .slice(0, MAX_PREWARM_CODE_BLOCKS);

  let jobCount = 0;

  for (const { code, language } of codeBlocks) {
    if (jobCount >= MAX_PREWARM_HIGHLIGHT_JOBS) return;

    const lines = splitCodeLinesForHighlighting(code);
    if (shouldEagerHighlightCode(code, lines.length)) {
      queueHighlightPrewarm({
        code,
        jobIndex: jobCount,
        language,
        theme,
      });
      jobCount += 1;
      continue;
    }

    for (const chunk of chunkCodeLinesForHighlighting(lines)) {
      if (jobCount >= MAX_PREWARM_HIGHLIGHT_JOBS) return;
      queueHighlightPrewarm({
        code: chunk,
        jobIndex: jobCount,
        language,
        theme,
      });
      jobCount += 1;
    }
  }
};
